import React, { useEffect, useRef } from 'react';
import {
  createFileRoute,
  useParams,
  useNavigate,
  useSearch,
} from '@tanstack/react-router';
import { UniversalDashboardRenderer } from '../../dashboard/components/common/UniversalDashboardRenderer';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { DashboardSelector } from '../../components/common/DashboardSelector';
import { DashboardProvider } from '../../dashboard/components/common/DashboardContext';
import { useSidebarControl } from '../../hooks/useSidebarControl';
import { useDashboard } from '../../api/dashboard';
import { useDashboardChat } from '../../dashboard/components/common/useDashboardChat';
import { MessageCircle } from 'lucide-react';
import { ScreenshotButton } from '../../components/Pleri/components/MessageContent/markdown/ScreenshotButton';
import { PDFButton } from '../../components/Pleri/components/MessageContent/markdown/PDFButton';
import { useChatStore } from '../../components/Pleri/store/chatStore';

import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/dashboard/$dashboardId')({
  component: DashboardDetailComponent,
});

interface _DashboardSearchParams {
  tenantId?: string;
  ownerId?: string;
  scope?: 'user' | 'team' | 'tenant';
  content?: string; // Base64 encoded dashboard config for legacy support
  fromQuickAction?: string; // Flag to indicate navigation from quick action
}

// Component that uses dashboard context for chat
function DashboardContent({
  dashboardId,
  dashboardQuery,
}: {
  dashboardId: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dashboardQuery: any;
}) {
  const { t } = useTranslation('pleri/pages');
  const dashboardRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const search = useSearch({
    from: '/dashboard/$dashboardId',
  }) as _DashboardSearchParams;
  const createConversation = useChatStore((state) => state.createConversation);
  const fromQuickAction = search.fromQuickAction === 'true';

  // Dashboard chat functionality for Ask Pleri button - now inside DashboardProvider
  const { openDashboardWithChat } = useDashboardChat({
    componentName: 'Dashboard',
    contextType: 'widget',
  });

  const handleChatClick = () => {
    openDashboardWithChat(
      undefined, // Use current dashboard ID from context
      `I'd like to understand this dashboard better. Can you help me analyze the data and insights?`,
    );
  };

  const handleBackClick = () => {
    if (fromQuickAction) {
      // Navigate to new chat conversation
      const newConversationId = createConversation();
      navigate({
        to: '/c/$conversationId',
        params: { conversationId: newConversationId },
      });
    } else {
      // Navigate to dashboard list
      navigate({
        to: '/dashboard',
      });
    }
  };

  const dashboardTitle =
    dashboardQuery.data?.dashboard?.title || t('dashboard');

  return (
    <div className="flex-1 overflow-auto">
      <div className="mb-4 p-6 pb-0">
        <div className="flex items-center justify-between">
          <button
            onClick={handleBackClick}
            className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            {fromQuickAction ? t('backToChat') : t('backToDashboards')}
          </button>

          {/* Center: Dashboard Selector */}
          <div className="w-64">
            <DashboardSelector currentDashboardId={dashboardId} />
          </div>

          {/* Right: Action buttons */}
          <div className="flex items-center gap-2">
            {/* Ask Pleri button */}
            <button
              onClick={handleChatClick}
              className="inline-flex items-center gap-2 rounded-lg bg-orange-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-orange-700 focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 focus:outline-none"
            >
              <MessageCircle size={16} />
              {t('askPleri')}
            </button>

            {/* Print buttons */}
            <ScreenshotButton
              targetRef={dashboardRef}
              filename={`${dashboardTitle.toLowerCase().replace(/\s+/g, '-')}-screenshot.png`}
              title={t('takeScreenshotOfDashboard')}
            />
            <PDFButton
              targetRef={dashboardRef}
              filename={`${dashboardTitle.toLowerCase().replace(/\s+/g, '-')}.pdf`}
              title={t('downloadDashboardAsPdf')}
            />
          </div>
        </div>
      </div>
      <div className="px-6 pb-6">
        <div ref={dashboardRef}>
          <UniversalDashboardRenderer
            dashboardId={dashboardId}
            variant="full"
            showActionToolbar={false}
            dashboardTitle={dashboardQuery.data?.dashboard?.title}
            dashboardScope={dashboardQuery.data?.dashboard?.scope}
          />
        </div>
      </div>
    </div>
  );
}

function DashboardDetailComponent() {
  const { dashboardId } = useParams({ from: '/dashboard/$dashboardId' });
  const { closeSidebar } = useSidebarControl();
  const dashboardQuery = useDashboard(dashboardId, 'user', 'user');

  // Close sidebar when viewing dashboard detail
  useEffect(() => {
    closeSidebar();
  }, [closeSidebar]);

  return (
    <DashboardProvider
      dashboardId={dashboardId}
      dashboardTitle={dashboardQuery.data?.dashboard?.title}
      dashboardScope={dashboardQuery.data?.dashboard?.scope}
    >
      <DashboardContent
        dashboardId={dashboardId}
        dashboardQuery={dashboardQuery}
      />
    </DashboardProvider>
  );
}
